import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { ExpensablesDataTableQuery } from 'generated/resources/datatable.graphql'
import { GroupDetailFragment } from 'generated/resources/graphql'

import DataTable, { useDataTable } from 'modules/core/components/DataTable'
import { DateNavigator } from 'modules/core/components/DateNavigator'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type { Expensable } from 'modules/expenses/components/expenses/DataTable/types'
import { useExportPdf } from 'modules/expenses/hooks/useGroup'
import { PageDateFilters } from 'modules/expenses/lib/PageDateFiltersContext'
import {
  canAddExpensableToGroup,
  canAddExpensableToPayment,
  canAddExpensableToPayroll,
  canRemoveExpensableFromGroup,
  canRemoveExpensableFromPayroll,
  canRestartExpensable,
  canRevertPaidExpensable,
  canReviewExpensable,
  canSetToPaidExpensable,
  useAvailableActions,
} from 'modules/expenses/lib/expensables/availableActions'
import {
  everyReimbursableCurrencyAllowed,
  showNotAllowedCurrenciesMessage,
} from 'modules/expenses/lib/expensables/expensable'
import { useExpenses } from 'modules/expenses/pages/ExpensesContext'

import { Options } from './_Actions/Options'
import { ActionFn } from './useActions'

interface HeaderProps {
  search: string
  onApprove: ActionFn
  onReject: ActionFn
  onRestartReview: ActionFn
  onAddToPayroll: ActionFn
  onAddToPayment: ActionFn
  onRemoveFromPayroll: ActionFn
  onSetToPaid: ActionFn
  onRevertPaid: ActionFn
  onAddToGroup: ActionFn
  onRemoveFromGroup: ActionFn
  onAddUsers?: () => void
  addUsersDisabled?: boolean
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void
  canRestartApprovalFlow: boolean
  canSetExpensableToPaid: boolean
  group?: GroupDetailFragment
  dateFilters?: PageDateFilters
  setPageDateFilters?: (startDate: Date, endDate: Date) => void
}

export const Header = ({
  search,
  onApprove,
  onReject,
  onRestartReview,
  onAddToPayroll,
  onAddToPayment,
  onRemoveFromPayroll,
  onSetToPaid,
  onRevertPaid,
  onAddToGroup,
  onRemoveFromGroup,
  onAddUsers,
  addUsersDisabled,
  onSearch,
  canRestartApprovalFlow,
  canSetExpensableToPaid,
  group,
  dateFilters,
  setPageDateFilters,
}: HeaderProps) => {
  const { employee, company } = useCurrentSession()
  const { data, selectedRows } = useDataTable<ExpensablesDataTableQuery, Expensable>()
  const { featureAvailabilities, data: expensesData } = useExpenses()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { exportPdf, loading: exportingPdf } = useExportPdf()
  const canManageExpenses = expensesData.expenses._policies.expenseManageExpenses.can

  const selectedIds = [...selectedRows].map(Number)
  const selectedExpensables = data.filter(({ id }) => selectedIds.includes(id))

  const availableActions = useAvailableActions(
    selectedExpensables,
    expensesData.expenses._policies.expenseRestartApprovalFlow.can,
    expensesData.expenses._policies.expenseSetToPaid.can
  )

  const searchParams = {
    value: search,
    onChange: onSearch,
    placeholder: i18n.t('common.search'),
  }

  const handleApprove = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canReviewExpensable(expensable, 'company')
    )

    onApprove(validExpensables)
  }

  const handleReject = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canReviewExpensable(expensable, 'company')
    )

    onReject(validExpensables)
  }

  const handleRestartReview = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canRestartExpensable(expensable, canRestartApprovalFlow, 'company')
    )

    onRestartReview(validExpensables)
  }

  const handleSetToPaid = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canSetToPaidExpensable(expensable, canSetExpensableToPaid, 'company')
    )

    onSetToPaid(validExpensables)
  }

  const handleRevertPaid = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canRevertPaidExpensable(expensable, canSetExpensableToPaid, 'company')
    )

    onRevertPaid(validExpensables)
  }

  const handleRemoveFromPayroll = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canRemoveExpensableFromPayroll(expensable, featureAvailabilities.reimburseThroughPayroll)
    )

    onRemoveFromPayroll(validExpensables)
  }

  const handleAddToPayroll = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canAddExpensableToPayroll(expensable, featureAvailabilities.reimburseThroughPayroll)
    )

    if (!everyReimbursableCurrencyAllowed(validExpensables)) {
      return showNotAllowedCurrenciesMessage()
    }

    onAddToPayroll(validExpensables)
  }

  const handleAddToPayment = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canAddExpensableToPayment(expensable, featureAvailabilities.reimburseThroughSepa)
    )

    onAddToPayment(validExpensables)
  }

  const handleAddToGroup = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canAddExpensableToGroup(
        featureAvailabilities.expensableGroups,
        expensable,
        'company',
        employee
      )
    )

    onAddToGroup(validExpensables)
  }

  const handleRemoveFromGroup = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canRemoveExpensableFromGroup(expensable, 'company')
    )

    onRemoveFromGroup(validExpensables)
  }

  const handleExportPDF = async () => {
    if (!group) return

    const handled = handleResult(await exportPdf(group.id, company.id), () => null)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('expenses_v2.group_detail.flash_messages.export_pdf.success'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('expenses_v2.group_detail.flash_messages.export_pdf.error'),
      })
    }
  }

  return (
    <Box gap="s16">
      <DataTable.HeadBar
        search={searchParams}
        exportFileName={i18n.t('expenses_v2.modals.export.file_name')}
        customPopoverOptions={
          <>
            {group && (
              <PopoverMenu.TouchableOption
                onClick={() => !exportingPdf && handleExportPDF()}
                title={i18n.t('expenses_v2.buttons.export_to_pdf')}
              />
            )}
            {canManageExpenses && (
              <PopoverMenu.TouchableOption
                title={i18n.t('expenses_v2.buttons.export')}
                description={i18n.t('expenses_v2.buttons.export_description')}
                onClick={() => {
                  navigate(navigation.expenses.list.expenses.exporter.fullPath())
                }}
              />
            )}
          </>
        }
        actions={
          <>
            {dateFilters && setPageDateFilters && (
              <DateNavigator
                startDate={dateFilters.startDate}
                endDate={dateFilters.endDate}
                onDateRangeChange={setPageDateFilters}
              />
            )}
            {onAddUsers && (
              <Button
                iconName="add"
                type="primary"
                onClick={onAddUsers}
                disabled={addUsersDisabled}
              >
                {i18n.t('billing.subscription.plans.expenses.actions.add_users')}
              </Button>
            )}
            {selectedExpensables.length > 0 && (
              <PopoverMenu type="list">
                <PopoverMenu.Trigger>
                  <Button.Decorative type="secondary">{i18n.t('common.actions')}</Button.Decorative>
                </PopoverMenu.Trigger>
                <PopoverMenu.Content side="bottom" align="end">
                  <Options
                    {...availableActions}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onRestartReview={handleRestartReview}
                    onSetToPaid={handleSetToPaid}
                    onRevertPaid={handleRevertPaid}
                    onRemoveFromPayroll={handleRemoveFromPayroll}
                    onAddToPayroll={handleAddToPayroll}
                    onAddToPayment={handleAddToPayment}
                    onAddToGroup={handleAddToGroup}
                    onRemoveFromGroup={handleRemoveFromGroup}
                  />
                </PopoverMenu.Content>
              </PopoverMenu>
            )}
          </>
        }
      />
    </Box>
  )
}
